import { useMemo, useState } from "react";
import {
  ArrowLeft,
  CalendarClock,
  ChevronRight,
  ClipboardList,
  ExternalLink,
  FileBarChart,
  HandCoins,
  Home,
  LayoutGrid,
  Package,
  UserRound,
  Users,
  UsersRound,
  Wallet,
} from "lucide-react";
import { useEmployeeLeftNavVisibility } from "../../../hooks/useEmployeeLeftNavVisibility";
import { getActiveWorkspaceKey } from "../../../nav/workspaceRegistry";
import MobileSheet from "./MobileSheet";
import { pickTabs, resolvePersona } from "./mobileTabs";

const DESK_PATHS = { review: "/admin/requests", approvals: "/admin/approvals" };

/** Destinations shared by bottom tabs and the More sheet. */
const DEST = {
  home: { label: "Home", icon: Home, to: "/admin/home", page: "Home" },
  requests: { label: "Requests", icon: ClipboardList, desk: "review" },
  attendance: { label: "Attendance", moreLabel: "Attendance logs", icon: CalendarClock, to: "/employee/attendance" },
  pay: { label: "Pay", moreLabel: "Personal payroll", icon: Wallet, to: "/employee/payroll" },
  profile: { label: "Profile", moreLabel: "My profile", icon: UserRound, to: "/employee/profile" },
  team: { label: "Team", moreLabel: "My team", icon: UsersRound, to: "/employee/my-team" },
  reports: { label: "Reports", moreLabel: "My reports", icon: FileBarChart, to: "/employee/reports" },
  assets: { label: "Assets", moreLabel: "My assets", icon: Package, to: "/employee/my-assets" },
  employees: { label: "Employees", icon: Users, to: "/admin/employee", page: "Employee" },
  adminAttendance: { label: "Attendance", icon: CalendarClock, to: "/admin/attendance", page: "Time & Attendance" },
  payrollAdmin: { label: "Payroll", icon: HandCoins, to: "/admin/payroll", page: "Payroll" },
};

const ESS_MORE_ORDER = ["home", "profile", "attendance", "pay", "team", "reports", "assets"];

const isOnPath = (pathname, to) => pathname === to || pathname.startsWith(`${to}/`);

function Tile({ icon: Icon, label, active, onClick, trailing }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className="group flex min-h-[84px] flex-col items-center justify-start gap-1.5 rounded-2xl px-1 py-2 text-center transition-transform duration-150 active:scale-95 motion-reduce:transition-none"
    >
      <span
        className={`relative flex h-12 w-12 items-center justify-center rounded-2xl transition-colors ${
          active
            ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
            : "bg-[hsl(var(--ds-primary)/0.08)] text-[hsl(var(--ds-primary))]"
        }`}
      >
        <Icon className="h-[22px] w-[22px]" strokeWidth={1.85} aria-hidden="true" />
        {trailing}
      </span>
      <span className="line-clamp-2 text-[12px] font-medium leading-tight text-[hsl(var(--ds-fg))]">
        {label}
      </span>
    </button>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-5 first:mt-1">
      <h3 className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--ds-muted-fg))]">
        {title}
      </h3>
      <div className="grid grid-cols-4 gap-1">{children}</div>
    </section>
  );
}

function Badge({ count }) {
  if (!count) return null;
  return (
    <span className="absolute -right-1.5 -top-1 min-w-[18px] rounded-full bg-[hsl(var(--ds-danger))] px-1 text-center text-[10px] font-bold leading-[18px] text-white ring-2 ring-[hsl(var(--ds-surface))]">
      {count > 99 ? "99+" : count}
    </span>
  );
}

/**
 * Phone shell: role-based bottom tabs, Request/Approvals switch and the More sheet.
 * Every list and handler comes from Sidebar — no separate permission logic here.
 */
export default function MobileTabBar({
  pathname,
  search = "",
  activeTabKey,
  onNavigate,
  adminModules,
  workspaces,
  onModuleOpen,
  onChildOpen,
  onWorkspaceOpen,
  canOpenActivityConsole,
  activityConsoleOpen,
  onActivityConsoleToggle,
  deskPanelKey,
  onOpenDesk,
  showRequests = true,
  approvalsBadge,
  usefulLinks,
  onUsefulLink,
}) {
  const activeWorkspaceKey = getActiveWorkspaceKey(pathname, search);
  const essNav = useEmployeeLeftNavVisibility();
  const [moreOpen, setMoreOpen] = useState(false);
  const [drillModule, setDrillModule] = useState(null);

  const adminNames = useMemo(() => adminModules.map((m) => m.name), [adminModules]);

  const isVisible = useMemo(() => {
    const map = {
      home: essNav.home,
      requests: showRequests,
      attendance: essNav.attendance,
      pay: essNav.payroll,
      profile: essNav.profile,
      team: essNav.myTeam,
      reports: essNav.reports,
      assets: essNav.myAssets,
      employees: adminNames.includes("Employee"),
      adminAttendance: adminNames.includes("Attendance") || adminNames.includes("Time & Attendance"),
      payrollAdmin: adminNames.includes("Payroll"),
    };
    return (key) => !!map[key];
  }, [essNav, adminNames, showRequests]);

  const tabs = useMemo(
    () => pickTabs(resolvePersona({ adminModuleNames: adminNames, hasReportees: essNav.myTeam }), isVisible),
    [adminNames, essNav.myTeam, isVisible]
  );

  const deskActive =
    !!deskPanelKey || pathname === DESK_PATHS.review || pathname === DESK_PATHS.approvals;
  const isTabActive = (key) => (key === "requests" ? deskActive : !!DEST[key].to && isOnPath(pathname, DEST[key].to));
  const moreActive = moreOpen || !tabs.some(isTabActive);

  const goTo = (key) => {
    const d = DEST[key];
    if (d.desk) onOpenDesk(d.desk);
    else onNavigate(d.to, d.page || d.moreLabel || d.label);
  };

  const essMore = ESS_MORE_ORDER.filter((key) => isVisible(key) && !tabs.includes(key));

  const closeMore = () => {
    setMoreOpen(false);
    setDrillModule(null);
  };

  const sheetHeader = drillModule ? (
    <div className="mt-3 flex items-center gap-2">
      <button
        type="button"
        onClick={() => setDrillModule(null)}
        className="-ml-2 inline-flex h-10 w-10 items-center justify-center rounded-full text-[hsl(var(--ds-fg))] active:bg-[hsl(var(--ds-primary)/0.1)]"
        aria-label="Back"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <h2 className="truncate text-lg font-semibold text-[hsl(var(--ds-fg))]">{drillModule.name}</h2>
    </div>
  ) : (
    <h2 className="mt-3 text-lg font-semibold text-[hsl(var(--ds-fg))]">More</h2>
  );

  return (
    <>
      {deskPanelKey ? (
        <div
          className="fixed inset-x-0 z-[10011] flex items-center border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3"
          style={{ top: "var(--app-header-height)", height: "var(--admin-secondary-nav-height)" }}
        >
          <div
            role="tablist"
            aria-label="Requests"
            className="grid h-10 w-full grid-cols-2 rounded-full bg-[hsl(var(--ds-primary)/0.08)] p-1"
          >
            {[
              { key: "review", label: "My requests" },
              { key: "approvals", label: "Approvals", badge: approvalsBadge },
            ].map((seg) => {
              const selected = deskPanelKey === seg.key;
              return (
                <button
                  key={seg.key}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => !selected && onOpenDesk(seg.key)}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-full text-sm font-semibold transition-colors duration-150 ${
                    selected
                      ? "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] shadow-sm"
                      : "text-[hsl(var(--ds-muted-fg))]"
                  }`}
                >
                  {seg.label}
                  {seg.badge ? (
                    <span className="min-w-[18px] rounded-full bg-[hsl(var(--ds-danger))] px-1 text-[10px] font-bold leading-[18px] text-white">
                      {seg.badge > 99 ? "99+" : seg.badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-[60] select-none border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface)/0.96)] backdrop-blur-md"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <ul className="grid h-16 grid-cols-5">
          {[...tabs, "more"].map((key) => {
            const isMore = key === "more";
            const d = isMore ? { label: "More", icon: LayoutGrid } : DEST[key];
            const active = isMore ? moreActive : !moreOpen && isTabActive(key);
            const Icon = d.icon;
            return (
              <li key={key} className="flex">
                <button
                  type="button"
                  onClick={() => (isMore ? setMoreOpen(true) : goTo(key))}
                  aria-current={active ? "page" : undefined}
                  className="flex flex-1 flex-col items-center justify-center gap-1 transition-transform duration-150 active:scale-95 motion-reduce:transition-none [-webkit-tap-highlight-color:transparent]"
                >
                  <span
                    className={`relative flex h-8 w-14 items-center justify-center rounded-full transition-colors duration-200 ${
                      active ? "bg-[hsl(var(--ds-primary)/0.14)] text-[hsl(var(--ds-primary))]" : "text-[hsl(var(--ds-muted-fg))]"
                    }`}
                  >
                    <Icon className="h-[22px] w-[22px]" strokeWidth={active ? 2.25 : 1.85} aria-hidden="true" />
                    {key === "requests" ? <Badge count={approvalsBadge} /> : null}
                  </span>
                  <span
                    className={`text-[11px] leading-none ${
                      active ? "font-semibold text-[hsl(var(--ds-primary))]" : "font-medium text-[hsl(var(--ds-muted-fg))]"
                    }`}
                  >
                    {d.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <MobileSheet open={moreOpen} onClose={closeMore} ariaLabel="More" header={sheetHeader}>
        {(closeThen) =>
          drillModule ? (
            <ul className="space-y-1 pt-1">
              {drillModule.children.map((child) => {
                const active = isOnPath(pathname, drillModule.to) && activeTabKey === child.key;
                return (
                  <li key={child.key}>
                    <button
                      type="button"
                      onClick={() => closeThen(() => onChildOpen(drillModule, child))}
                      aria-current={active ? "page" : undefined}
                      className={`flex min-h-[48px] w-full items-center justify-between rounded-xl px-4 text-left text-[15px] transition-colors ${
                        active
                          ? "bg-[hsl(var(--ds-primary)/0.12)] font-semibold text-[hsl(var(--ds-primary))]"
                          : "text-[hsl(var(--ds-fg))] active:bg-[hsl(var(--ds-primary)/0.08)]"
                      }`}
                    >
                      {child.label}
                      <ChevronRight className="h-4 w-4 shrink-0 text-[hsl(var(--ds-muted-fg))]" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <>
              {essMore.length > 0 && (
                <Section title="My space">
                  {essMore.map((key) => (
                    <Tile
                      key={key}
                      icon={DEST[key].icon}
                      label={DEST[key].moreLabel || DEST[key].label}
                      active={isOnPath(pathname, DEST[key].to)}
                      onClick={() => closeThen(() => goTo(key))}
                    />
                  ))}
                </Section>
              )}

              {(adminModules.length > 0 || canOpenActivityConsole) && (
                <Section title="Admin">
                  {adminModules.map((item) => (
                    <Tile
                      key={item.name}
                      icon={item.icon}
                      label={item.name}
                      active={!activeWorkspaceKey && isOnPath(pathname, item.to)}
                      onClick={() =>
                        item.children?.length
                          ? setDrillModule(item)
                          : closeThen(() => onModuleOpen(item))
                      }
                    />
                  ))}
                  {canOpenActivityConsole && (
                    <Tile
                      icon={ClipboardList}
                      label="Action Hub"
                      active={activityConsoleOpen}
                      onClick={() => closeThen(() => onActivityConsoleToggle(activityConsoleOpen))}
                    />
                  )}
                </Section>
              )}

              {workspaces.length > 0 && (
                <Section title="Workspaces">
                  {workspaces.map((ws) => (
                    <Tile
                      key={ws.key}
                      icon={ws.icon}
                      label={ws.label}
                      active={activeWorkspaceKey === ws.key}
                      onClick={() => closeThen(() => onWorkspaceOpen(ws))}
                    />
                  ))}
                </Section>
              )}

              {[
                { key: "employee", title: "Useful links · Employee" },
                { key: "employer", title: "Useful links · Employer" },
              ].map(({ key, title }) =>
                usefulLinks?.[key]?.length ? (
                  <section key={key} className="mt-5">
                    <h3 className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--ds-muted-fg))]">
                      {title}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {usefulLinks[key].map((link) => (
                        <button
                          key={link.key}
                          type="button"
                          onClick={() => onUsefulLink(link)}
                          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-[hsl(var(--ds-border))] px-3 text-[13px] text-[hsl(var(--ds-fg))] active:bg-[hsl(var(--ds-primary)/0.08)]"
                        >
                          {link.label}
                          <ExternalLink className="h-3.5 w-3.5 text-[hsl(var(--ds-muted-fg))]" aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                  </section>
                ) : null
              )}
            </>
          )
        }
      </MobileSheet>
    </>
  );
}
